'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useUserResourceSource } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import {
  AnonymousFallback,
  ButtonSkeleton,
  IslandKeyframes,
  ToggleErrorCaption,
  toggleActive,
  toggleError,
  toggleInactive,
} from './_shared';
import { useToggleErrorMessage } from './useToggleErrorMessage';

export interface LikeButtonProps {
  videoSlug: string;
  signInHref?: string;
}

/**
 * Thumbs-up like toggle for a video. Mirrors {@link WatchLaterButton}:
 * optimistic flip, inline error caption, anonymous "sign in" fallback.
 *
 * <p>The upstream exposes no per-user liked flag ({@code likes.has}
 * returns {@code null}), so the button always starts unpressed and
 * reflects the toggle result thereafter.
 */
export function LikeButton({ videoSlug, signInHref }: LikeButtonProps) {
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const [toggling, setToggling] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const { t } = useTranslation('site-builder-blocks');
  const { error, setError, clearError } = useToggleErrorMessage();

  const state = useAsyncResource<boolean>(
    async (signal) => {
      if (!user || !ur?.likes) return null;
      return (await ur.likes.has(videoSlug, signal)) ?? false;
    },
    [user, ur, videoSlug],
  );

  if (!user)
    return (
      <>
        <IslandKeyframes />
        <AnonymousFallback
          variant="button"
          prompt={t('islands.signInToLike')}
          {...(signInHref ? { signInHref } : {})}
        />
      </>
    );
  if (!ur?.likes) return null;
  if (state.kind === 'loading')
    return (
      <>
        <IslandKeyframes />
        <ButtonSkeleton width="110px" />
      </>
    );

  const liked = optimistic ?? (state.kind === 'loaded' ? state.data : false);
  const handle = async () => {
    if (toggling) return;
    clearError();
    setToggling(true);
    setOptimistic(!liked);
    try {
      const res = await ur.likes!.toggle({ videoSlug });
      if (!res || res.skipped) setOptimistic(null);
      else setOptimistic(Boolean(res.active));
    } catch (err) {
      // Roll back optimistic state and surface the failure inline.
      setOptimistic(null);

      console.error('[LikeButton] toggle failed', err);
      setError(t('islands.toggleFailed'));
    } finally {
      setToggling(false);
    }
  };

  if (state.kind === 'error')
    return (
      <button type="button" onClick={() => location.reload()} style={toggleError}>
        {t('islands.like.retry')}
      </button>
    );

  return (
    <>
      <button
        type="button"
        onClick={handle}
        disabled={toggling}
        aria-pressed={liked}
        style={liked ? toggleActive : toggleInactive}
      >
        {liked ? t('islands.like.liked') : t('islands.like.like')}
      </button>
      {error && <ToggleErrorCaption message={error} />}
    </>
  );
}
